import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { useFeature } from 'flagged'

import MobileNavModal from 'shared/mobile-nav-modal'
import StoreSearchButton from 'store/components/search-button'
import CartSummary from 'cart/components/cart-summary'
import classNames from 'classnames'
import { camelCase, mapKeys } from 'lodash'
import { faqPath, indexPath, newRegistryPath, registryHomePath } from 'lib/urls'
import { initializePurchasingStore } from 'cart/lib'
import {
  ACTIVE_NAV_SECTION_EVENT,
  HIDE_MOBILE_NAV_MODAL_EVENT,
} from 'shared/constants'
import { track, useTracking, withContextualizedTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { setCurrentUser } from 'shared/utils/setCurrentUser/setCurrentUser'
import css from './global-nav.scss'
import AccountMenu from './components/AccountMenu'
import ToggleNav from './components/ToggleNav'
import SiteNavSection from './components/SiteNavSection'
import withCurrentUser from 'shared/HOC/withCurrentUser'
import MessageCenterMenu from 'components/global-nav/components/MessageCenterMenu'

@withContextualizedTracking((props) => {
  // use to track cart opened eventLocation
  const { activeNavItem } = props
  const eventLocation = (function chooseEventLocation(_eventLocation) {
    switch (_eventLocation) {
      case 'Home':
        return track.EventLocation.HOME_PAGE
      case 'Registry':
        return track.EventLocation.REGISTRY
      case 'Holiday':
        return track.EventLocation.NONE
      case 'Guides':
        return track.EventLocation.GUIDE
      case 'Store':
        return track.EventLocation.STORE
      default:
        return track.EventLocation.NONE
    }
  })(activeNavItem)
  return { eventLocation }
})
class GlobalNav extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      activeNavItem: props.activeNavItem,
      sideNavVisible: false,
    }
    this.handleToggleNav = this.handleToggleNav.bind(this)
  }

  componentDidMount() {
    PubSub.subscribe(HIDE_MOBILE_NAV_MODAL_EVENT, () => {
      this.setState({ sideNavVisible: false })
    })
    PubSub.subscribe(ACTIVE_NAV_SECTION_EVENT, (event) => {
      this.setState({ activeNavItem: event })
    })
    PubSub.subscribe('switchNavState', (user) => {
      const formattedUser = mapKeys(user, (_, key) => camelCase(key))
      setCurrentUser(formattedUser)
    })
  }

  handleToggleNav() {
    this.setState({
      sideNavVisible: !this.state.sideNavVisible,
    })
  }

  render() {
    const {
      currentCart,
      currentUser,
      hideCartSummary,
      isRegistryGuestView,
      showMinimalView,
      showAcquisitionView,
      isLoggedIn,
      ...restOfProps
    } = this.props

    const { activeNavItem, sideNavVisible } = this.state

    return (
      <StatelessGlobalNav
        activeNavItem={activeNavItem}
        currentCart={currentCart}
        currentUser={currentUser}
        hideCartSummary={hideCartSummary}
        isRegistryGuestView={isRegistryGuestView}
        showMinimalView={showMinimalView}
        showAcquisitionView={showAcquisitionView}
        isLoggedIn={isLoggedIn}
        sideNavVisible={sideNavVisible}
        handleToggleNav={this.handleToggleNav}
        {...restOfProps}
      />
    )
  }
}

GlobalNav.propTypes = {
  activeNavItem: PropTypes.string,
  currentCart: PropTypes.object,
  showMinimalView: PropTypes.bool,
  userCanViewRegistry: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
}

const StatelessGlobalNav = (props) => {
  const {
    currentCart,
    currentUser,
    hideCartSummary,
    isRegistryGuestView,
    showMinimalView,
    showAcquisitionView,
    isLoggedIn,
    activeNavItem,
    sideNavVisible,
    handleToggleNav,
  } = props

  const excludedFromC6UnifiedSearchPhase1 = showAcquisitionView

  const includedInC6UnifiedSearchPhase1 = !excludedFromC6UnifiedSearchPhase1

  const c6UnifiedSearchPhase0 = useFeature('c6_unified_search_phase_0')

  const c6UnifiedSearchPhase1Control =
    excludedFromC6UnifiedSearchPhase1 ||
    (includedInC6UnifiedSearchPhase1 && !c6UnifiedSearchPhase0)

  const c6UnifiedSearchPhase0Variant =
    includedInC6UnifiedSearchPhase1 && c6UnifiedSearchPhase0

  const messageCenterEnabled = useFeature('message_center_enabled')

  const tracker = useTracking()

  const trackClick = (eventCta) => {
    tracker.trackEvent({
      event: track.navbarClicked,
      eventCta,
      eventLocation: track.EventLocation.WEB_TOP_NAV,
    })
  }

  return (
    <nav className={classNames(css.globalNav, css.withShadow)}>
      <div
        className={classNames([
          'container',
          {
            [css.GlobalNav__Container]: c6UnifiedSearchPhase0Variant,
            [css.GlobalNav__MessageCenterContainer]: messageCenterEnabled,
          },
        ])}
      >
        <div className={css.flexRow}>
          {c6UnifiedSearchPhase0Variant && (
            <div
              className={classNames({
                [css.farLeft]: true,
                [css.GlobalNav__Logo]: c6UnifiedSearchPhase0Variant,
              })}
            >
              <Logo trackClick={trackClick} />
            </div>
          )}

          <SiteNavSection
            activeNavItem={activeNavItem}
            classNames={{
              [css.farLeft]: c6UnifiedSearchPhase1Control,
              [css.GlobalNav__SiteNavSection]: c6UnifiedSearchPhase0Variant,
            }}
            handleToggleNav={handleToggleNav}
            isLoggedIn={isLoggedIn || !!currentUser}
            showAcquisitionView={showAcquisitionView}
            showMinimalView={isRegistryGuestView || showMinimalView}
            trackClick={trackClick}
          />

          {c6UnifiedSearchPhase0Variant && (
            <div className={classNames(css.GlobalNav__Search)} />
          )}

          {c6UnifiedSearchPhase1Control && (
            <div className={classNames(css.middle)}>
              <Logo trackClick={trackClick} />
            </div>
          )}

          <ul
            className={classNames({
              [css.farRight]: c6UnifiedSearchPhase1Control,
              [css.GlobalNav__ActionsNavSection]:
                c6UnifiedSearchPhase0Variant || messageCenterEnabled,
            })}
          >
            <ActionsNavSection
              cart={currentCart}
              hideCartSummary={hideCartSummary}
              isLoggedIn={isLoggedIn || !!currentUser}
              messageCenterEnabled={messageCenterEnabled}
              showAcquisitionView={showAcquisitionView}
              showMinimalView={isRegistryGuestView || showMinimalView}
              showSearch={activeNavItem === 'Guides'}
              c6UnifiedSearchPhase0Variant={c6UnifiedSearchPhase0Variant}
              trackClick={trackClick}
            />
          </ul>
          <MobileNavModal
            {...props}
            show={sideNavVisible}
            onHide={handleToggleNav}
          />
        </div>
      </div>
    </nav>
  )
}

const ActionsNavSection = ({
  cart,
  hideCartSummary,
  showMinimalView,
  isLoggedIn,
  messageCenterEnabled,
  showSearch,
  showAcquisitionView,
  c6UnifiedSearchPhase0Variant,
  trackClick,
}) => {
  const reservations = cart.reservations.map((res) =>
    initializePurchasingStore(res)
  )

  return (
    <>
      {!showAcquisitionView &&
        (!isLoggedIn ||
          (!c6UnifiedSearchPhase0Variant && !messageCenterEnabled)) && (
          <li className={classNames(css.navLink, 'hidden-xs')}>
            <a
              target="_blank"
              href={faqPath}
              onClick={() => trackClick('Help')}
              rel=" noopener noreferrer"
            >
              Help
            </a>
          </li>
        )}

      {!showMinimalView &&
        (isLoggedIn ? (
          <li
            className={classNames(css.navLink, {
              [css.GlobalNav__AccountMenu]: c6UnifiedSearchPhase0Variant,
            })}
          >
            <AccountMenu
              c6UnifiedSearchPhase0Variant={c6UnifiedSearchPhase0Variant}
              trackNavbarClicked={trackClick}
            />
          </li>
        ) : (
          // logged out
          <>
            <li className={classNames('hidden-xs', css.navLink)}>
              <a href="javascript:PubSub.publish ('authModal.show')">Log In</a>
            </li>
            <li
              className={classNames(
                'hidden-xs btn-pill btn btn-sm btn-info',
                css.navLink,
                css.navButton
              )}
            >
              <a href={newRegistryPath} onClick={() => trackClick('Sign Up')}>
                Sign Up
              </a>
            </li>
          </>
        ))}

      {showSearch && (
        <li className={classNames(css.navIcnBtn, 'hidden-md', 'visible-xs')}>
          <StoreSearchButton />
        </li>
      )}

      {messageCenterEnabled && isLoggedIn && (
        <li
          className={classNames(css.navIcnBtn, css.GlobalNav__MessageCenter)}
          style={{ position: 'relative' }}
        >
          <MessageCenterMenu
            c6UnifiedSearchPhase0Variant={c6UnifiedSearchPhase0Variant}
          />
        </li>
      )}

      {!showAcquisitionView && (
        <li className={css.navIcnBtn} style={{ position: 'relative' }}>
          <CartSummary
            cart={cart.cart}
            disableDropdown={hideCartSummary}
            registryUrlSlug={cart.registryUrlSlug}
            reservations={reservations}
            c6UnifiedSearchPhase0Variant={c6UnifiedSearchPhase0Variant}
          />
        </li>
      )}
    </>
  )
}

ActionsNavSection.propTypes = {
  cart: PropTypes.object,
  showMinimalView: PropTypes.bool,
  messageCenterEnabled: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showSearch: PropTypes.bool,
  showAcquisitionView: PropTypes.bool,
}

const Logo = ({ className, trackClick }) => {
  const [currentUser] = useCurrentUser()

  return (
    <div className={classNames(css.logo, className)}>
      <a
        href={currentUser ? registryHomePath : indexPath}
        onClick={() => trackClick('Logo')}
      >
        Babylist - Universal Baby Registry
      </a>
    </div>
  )
}

Logo.propTypes = {
  className: PropTypes.string,
  user: PropTypes.object,
}

export default withCurrentUser(GlobalNav)
export { SiteNavSection, ActionsNavSection, Logo, ToggleNav }
